/**
 * Parse le commentaire / options d'une ligne commande pour affichage ticket / modal commerçant.
 */

function splitIngredientList(raw) {
  if (!raw) return [];
  return String(raw)
    .split(/[,;]/)
    .map((s) => s.trim().replace(/\.$/, ''))
    .filter(Boolean);
}

/** Ex. « Coppa di Parma ×2 » → { nom, qty } */
export function parseIngredientToken(line) {
  const text = String(line || '').trim();
  if (!text) return null;

  const withQty = text.match(/^(.+?)\s*[×x]\s*(\d+)\s*$/i);
  if (withQty) {
    return {
      nom: withQty[1].trim(),
      qty: Math.max(1, parseInt(withQty[2], 10) || 1),
    };
  }

  return { nom: text, qty: 1 };
}

function normalizeIngredientsList(items) {
  if (!Array.isArray(items)) return [];
  return items
    .map((item) => {
      if (item && typeof item === 'object' && item.nom) {
        return {
          nom: String(item.nom).trim(),
          qty: Math.max(1, Number(item.qty) || 1),
        };
      }
      return parseIngredientToken(item);
    })
    .filter(Boolean);
}

function parseBulletBlock(text) {
  const marker = text.search(/Ingrédients?\s*:/i);
  if (marker === -1) return null;

  const header = text.slice(0, marker).trim();
  const body = text.slice(marker).replace(/^Ingrédients?\s*:\s*/i, '').trim();
  const lines = body
    .split('\n')
    .map((l) => l.replace(/^[\s•·\-–—]+\s*/, '').trim())
    .filter(Boolean);

  if (lines.length > 0) {
    return { header: header || null, ingredients: normalizeIngredientsList(lines) };
  }

  const fromCommas = splitIngredientList(body);
  if (fromCommas.length > 0) {
    return { header: header || null, ingredients: normalizeIngredientsList(fromCommas) };
  }

  return null;
}

/** Options web checkout : [{ optionId, optionNom, nom, quantite }] */
export function extractPlancheIngredientsFromOptions(options) {
  if (!options) return [];

  let parsed = options;
  if (typeof options === 'string') {
    try {
      parsed = JSON.parse(options);
    } catch {
      return [];
    }
  }

  if (!Array.isArray(parsed)) {
    if (parsed && typeof parsed === 'object' && parsed['ingredients-planche']?.choix) {
      return normalizeIngredientsList(
        parsed['ingredients-planche'].choix.map((c) => ({
          nom: c.nom,
          qty: c.count ?? c.quantite ?? 1,
        }))
      );
    }
    return [];
  }

  const plancheRows = parsed.filter(
    (row) =>
      row?.optionId === 'ingredients-planche' ||
      /ingrédient/i.test(String(row?.optionNom || row?.nom || ''))
  );

  if (plancheRows.length === 0) return [];

  return normalizeIngredientsList(
    plancheRows.map((row) => ({
      nom: row.nom,
      qty: row.quantite ?? row.count ?? 1,
    }))
  );
}

function mergeIngredientQuantities(fromCommentaire, fromOptions) {
  if (!fromOptions?.length) return fromCommentaire;

  const qtyByNom = new Map(
    fromOptions.map((o) => [o.nom.toLocaleLowerCase('fr'), o.qty])
  );

  const merged = (fromCommentaire || []).map((ing) => {
    const key = ing.nom.toLocaleLowerCase('fr');
    const qty = qtyByNom.get(key) ?? ing.qty;
    return { nom: ing.nom, qty };
  });

  if (merged.length === 0) return fromOptions;
  return merged;
}

export function parseLigneCommentaire(commentaire) {
  const text = String(commentaire || '').trim();
  if (!text) return null;

  if (text.includes('\n') || /Ingrédients?\s*:/i.test(text)) {
    const bullet = parseBulletBlock(text);
    if (bullet) {
      const persMatch = (bullet.header || text).match(
        /Planche personnalisée\s*[–—-]\s*(\d+)\s*personne?s?/i
      );
      return {
        kind: 'planche_perso',
        nbPersons: persMatch ? parseInt(persMatch[1], 10) : null,
        ingredients: bullet.ingredients,
      };
    }

    return {
      kind: 'multiline',
      lines: text.split('\n').map((l) => l.trim()).filter(Boolean),
    };
  }

  const legacyPerso = text.match(
    /^Planche personnalisée\s*[–—-]\s*(\d+)\s*personne?s?\.\s*Ingrédients choisis\s*:\s*(.+?)\.?\s*$/i
  );
  if (legacyPerso) {
    return {
      kind: 'planche_perso',
      nbPersons: parseInt(legacyPerso[1], 10),
      ingredients: normalizeIngredientsList(splitIngredientList(legacyPerso[2])),
    };
  }

  const variee = text.match(/^Planche variée\s*[–—-]\s*(\d+)\s*personne?s?\.?$/i);
  if (variee) {
    return {
      kind: 'planche_variee',
      nbPersons: parseInt(variee[1], 10),
    };
  }

  const comp = text.match(/^(.+?)\s*[–—-]\s*(.+)$/);
  if (comp && comp[2].includes('×')) {
    return {
      kind: 'composition',
      title: comp[1].trim(),
      items: comp[2]
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean),
    };
  }

  return { kind: 'plain', text };
}

/** Commentaire + options JSON (quantités checkout web). */
export function parseLigneCommandeDetails(commentaire, options) {
  const fromOptions = extractPlancheIngredientsFromOptions(options);
  const parsed = parseLigneCommentaire(commentaire);

  if (parsed?.kind === 'planche_perso') {
    return {
      ...parsed,
      ingredients: mergeIngredientQuantities(parsed.ingredients, fromOptions),
    };
  }

  if (!parsed && fromOptions.length > 0) {
    return {
      kind: 'planche_perso',
      nbPersons: null,
      ingredients: fromOptions,
    };
  }

  return parsed;
}

export function buildPlancheCommentaire({ mode, nbPersons, ingredients = [] }) {
  const persLabel = nbPersons
    ? `${nbPersons} personne${nbPersons > 1 ? 's' : ''}`
    : '';

  if (mode === 'VARIEE') {
    return `Planche variée — ${persLabel}`.trim();
  }

  const header = `Planche personnalisée — ${persLabel}`.trim();
  const rows = normalizeIngredientsList(ingredients);

  if (rows.length === 0) {
    return `${header}\n\nIngrédients :\n• (aucun ingrédient sélectionné)`;
  }

  const lines = rows.map(({ nom, qty }) =>
    qty > 1 ? `• ${nom} ×${qty}` : `• ${nom}`
  );
  return `${header}\n\nIngrédients :\n${lines.join('\n')}`;
}
